import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { Settings as SettingsIcon, Save, Shield } from 'lucide-react';

export default function Settings() {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/api/config').then(r => { setConfig(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/api/config', config);
      setConfig(res.data);
      alert('Settings saved!');
    } catch (err) { alert(err.response?.data?.detail || 'Error saving'); }
    finally { setSaving(false); }
  };

  if (user?.role !== 'admin') return <div className="glass rounded-xl p-8 text-center"><Shield className="w-12 h-12 text-dark-500 mx-auto mb-3" /><p className="text-dark-400">Admin access required</p></div>;
  if (loading) return <div className="flex justify-center h-32"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div></div>;

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <div><h3 className="text-xl font-bold text-white">Settings</h3><p className="text-dark-400 text-sm">Configure branding and business rules</p></div>
        <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-dark-900 font-medium rounded-lg text-sm disabled:opacity-50"><Save className="w-4 h-4" />{saving ? 'Saving...' : 'Save Changes'}</button>
      </div>

      <div className="glass rounded-xl p-5 space-y-4">
        <h4 className="text-white font-medium border-b border-dark-700 pb-2">App Branding</h4>
        <div>
          <label className="block text-sm text-dark-300 mb-2">App Logo</label>
          <div className="flex items-center gap-4">
            <img src="/logo.jpg" alt="Current Logo" className="w-16 h-16 rounded-xl object-cover border border-dark-600" onError={e => e.target.style.display='none'} />
            <div>
              <input type="file" accept="image/*" id="logo-upload" className="hidden" onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                const formData = new FormData();
                formData.append('file', file);
                try {
                  await api.post('/api/config/upload-logo', formData, {headers:{'Content-Type':'multipart/form-data'}});
                  alert('Logo updated! Refresh the page to see it.');
                  window.location.reload();
                } catch(err) { alert('Error uploading logo'); }
              }} />
              <label htmlFor="logo-upload" className="cursor-pointer px-4 py-2 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 rounded-lg text-sm border border-blue-500/20">
                📁 Upload New Logo
              </label>
              <p className="text-dark-500 text-xs mt-1">JPG, PNG, SVG supported</p>
            </div>
          </div>
        </div>
        <div>
          <label className="block text-sm text-dark-300 mb-2">Logo Shape</label>
          <div className="flex gap-3">
            {[
              {val:'rectangle', label:'Rectangle', cls:'w-16 h-10 rounded-xl'},
              {val:'square',    label:'Square',    cls:'w-12 h-12 rounded-xl'},
              {val:'circle',    label:'Circle',    cls:'w-12 h-12 rounded-full'},
            ].map(({val,label,cls}) => (
              <button key={val} onClick={() => setConfig({...config, logo_shape: val})}
                className={`flex flex-col items-center gap-2 p-3 rounded-lg border transition-colors ${config?.logo_shape === val || (!config?.logo_shape && val==='square') ? 'border-primary-500 bg-primary-500/10' : 'border-dark-600 bg-dark-800 hover:border-dark-500'}`}>
                <div className={`${cls} bg-dark-600 overflow-hidden border border-dark-500`}>
                  <img src="/logo.jpg" className="w-full h-full object-cover" alt="" />
                </div>
                <span className="text-xs text-dark-300">{label}</span>
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-sm text-dark-300 mb-2">Favicon (Browser Tab Icon)</label>
          <div className="flex items-center gap-4">
            <img src="/favicon.svg" alt="Current Favicon" className="w-10 h-10 rounded object-cover border border-dark-600" onError={e => e.target.style.display='none'} />
            <div>
              <input type="file" accept="image/*,.svg,.ico" id="favicon-upload" className="hidden" onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                const formData = new FormData();
                formData.append('file', file);
                try {
                  await api.post('/api/config/upload-favicon', formData, {headers:{'Content-Type':'multipart/form-data'}});
                  alert('Favicon updated! Refresh to see it in the browser tab.');
                  window.location.reload();
                } catch(err) { alert('Error uploading favicon'); }
              }} />
              <label htmlFor="favicon-upload" className="cursor-pointer px-4 py-2 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 rounded-lg text-sm border border-blue-500/20">
                📁 Upload Favicon
              </label>
              <p className="text-dark-500 text-xs mt-1">ICO, PNG, SVG supported. Recommended: 32×32px</p>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="col-span-2 flex gap-4 mb-2">
            <label className="flex items-center gap-2 text-sm text-dark-300 cursor-pointer">
              <input type="checkbox" checked={config?.show_app_name !== false} onChange={e => setConfig({...config, show_app_name: e.target.checked})} className="rounded" />
              Show App Name in Sidebar
            </label>
            <label className="flex items-center gap-2 text-sm text-dark-300 cursor-pointer">
              <input type="checkbox" checked={config?.show_tagline !== false} onChange={e => setConfig({...config, show_tagline: e.target.checked})} className="rounded" />
              Show Tagline in Sidebar
            </label>
          </div>
          <SettingField label="App Name" value={config?.app_name || ''} onChange={v => setConfig({...config, app_name: v})} />
          <SettingField label="Dashboard Headline" value={config?.dashboard_headline || ''} onChange={v => setConfig({...config, dashboard_headline: v})} />
          <SettingField label="Total Sales Display" value={config?.total_sales_display || ''} onChange={v => setConfig({...config, total_sales_display: v})} />
          <SettingField label="Total Properties Display" value={config?.total_properties_display || ''} onChange={v => setConfig({...config, total_properties_display: v})} />
        </div>
      </div>

      <div className="glass rounded-xl p-5 space-y-4">
        <h4 className="text-white font-medium border-b border-dark-700 pb-2">Meeting & Payout Settings</h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <SettingField label="Monthly Target Meetings" value={config?.monthly_target_meetings?.toString() || ''} onChange={v => setConfig({...config, monthly_target_meetings: parseInt(v) || 0})} type="number" />
          <SettingField label="Base Payout / Meeting (Rs.)" value={config?.base_payout_per_meeting?.toString() || ''} onChange={v => setConfig({...config, base_payout_per_meeting: parseFloat(v) || 0})} type="number" />
          <SettingField label="Bonus Above Target (Rs.)" value={config?.bonus_payout_above_target?.toString() || ''} onChange={v => setConfig({...config, bonus_payout_above_target: parseFloat(v) || 0})} type="number" />
        </div>
      </div>

      <div className="glass rounded-xl p-5 space-y-4">
        <h4 className="text-white font-medium border-b border-dark-700 pb-2">Commission Settings</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SettingField label="Commission % (Primary)" value={config?.commission_percentage?.toString() || ''} onChange={v => setConfig({...config, commission_percentage: parseFloat(v) || 0})} type="number" />
          <SettingField label="Commission % (Alt)" value={config?.commission_percentage_alt?.toString() || ''} onChange={v => setConfig({...config, commission_percentage_alt: parseFloat(v) || 0})} type="number" />
        </div>
      </div>

      <div className="glass rounded-xl p-5 space-y-4">
        <h4 className="text-white font-medium border-b border-dark-700 pb-2">Lead Re-Churn Settings</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SettingField label="Re-churn After (Days)" value={config?.rechurn_days?.toString() || ''} onChange={v => setConfig({...config, rechurn_days: parseInt(v) || 15})} type="number" />
        </div>
      </div>
      <div className="glass rounded-xl p-5 space-y-4">
        <h4 className="text-white font-medium border-b border-dark-700 pb-2">Currency Settings (Admin Only)</h4>
        <p className="text-dark-400 text-xs">All values display in AED by default. Toggle to show INR equivalent for executives.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-dark-300 mb-1">Display Currency</label>
            <select value={config?.currency || 'AED'} onChange={e => setConfig({...config, currency: e.target.value})} className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm">
              <option value="AED">AED (Default)</option>
              <option value="INR">INR (Show Rupees)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-dark-300 mb-1">AED to INR Rate</label>
            <div className="flex gap-2">
              <input type="number" value={config?.aed_to_inr_rate || 22.5} onChange={e => setConfig({...config, aed_to_inr_rate: parseFloat(e.target.value) || 22.5})} className="flex-1 px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm" />
              <button onClick={async () => {
                try {
                  const res = await (await import('../utils/api')).default.get('/api/config/exchange-rate');
                  setConfig(c => ({...c, aed_to_inr_rate: res.data.aed_to_inr_rate}));
                  alert('Rate updated: 1 AED = ' + res.data.aed_to_inr_rate.toFixed(2) + ' INR');
                } catch(e) { alert('Failed to fetch live rate'); }
              }} className="px-3 py-2 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 rounded-lg text-sm whitespace-nowrap">🔄 Fetch Live Rate</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SettingField({ label, value, onChange, type = 'text' }) {
  return (
    <div>
      <label className="block text-sm text-dark-300 mb-1">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} className="w-full px-3 py-2.5 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500" />
    </div>
  );
}
